import { Link } from 'react-router-dom';

import { money } from '../components/CostCard';
import { Card, Empty, Meter, StatTile, StatusPill } from '../components/ui';
import { api } from '../lib/api';
import { ago, bytes, ratio } from '../lib/format';
import { useApi } from '../lib/useApi';

interface OverviewData {
  clients: number;
  servers: { total: number; up: number; degraded: number; unreachable: number; unknown: number };
  deployments: number;
  containers: { total: number; byState: Record<string, number> };
  resources: Array<{
    serverId: string;
    name: string;
    hostname: string;
    status: string;
    cpuPercent: string | null;
    memUsedBytes: string | null;
    memTotalBytes: string | null;
    diskUsedBytes: string | null;
    diskTotalBytes: string | null;
    sampledAt: string | null;
    monthlyCost: string | null;
    costCurrency: string | null;
  }>;
  cost: {
    base: string;
    totalCad: number | null;
    byCurrency: Array<{ currency: string; total: number }>;
    rates: Record<string, number>;
    ratesAsAt: string | null;
    unpriced: number;
    missingRates: string[];
  };
  recentFailures: Array<{ server: string; hostname: string; at: string; error: string | null }>;
}

export function Overview() {
  const { data, error, loading, reload } = useApi<OverviewData>('/overview');

  if (loading && !data) return <p className="empty">Loading the estate…</p>;
  if (error && !data) return <p className="form-error">{error}</p>;
  if (!data) return null;

  const running = data.containers.byState.RUNNING ?? 0;
  const notRunning = data.containers.total - running;

  return (
    <>
      <div className="page-head">
        <h1>Estate overview</h1>
        <button
          type="button"
          className="ghost"
          onClick={async () => {
            // Reaches every monitored host over SSH, so it is a deliberate press
            // rather than something the page does on its own.
            await api('/collect', { method: 'POST' });
            reload();
          }}
        >
          Collect now
        </button>
      </div>

      <div className="tiles">
        <StatTile label="Clients" value={data.clients} />
        <StatTile
          label="Servers"
          value={data.servers.total}
          sub={`${data.servers.up} up`}
          tone={data.servers.unreachable > 0 ? 'down' : data.servers.unknown > 0 ? 'warn' : 'up'}
        />
        <StatTile label="Deployments" value={data.deployments} />
        <StatTile
          label="Containers"
          value={data.containers.total}
          sub={notRunning > 0 ? `${notRunning} not running` : 'all running'}
          tone={notRunning > 0 ? 'warn' : 'up'}
        />
      </div>

      <Card title="Servers">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Server</th>
                <th>Status</th>
                <th>CPU</th>
                <th>Memory</th>
                <th>Disk</th>
                <th className="num">Monthly cost</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.resources.map((r) => (
                <tr key={r.serverId}>
                  <td>
                    <Link to={`/servers/${r.serverId}`}>{r.name}</Link>
                    <div className="muted mono">{r.hostname}</div>
                  </td>
                  <td><StatusPill status={r.status} /></td>
                  <td><Meter value={r.cpuPercent === null ? null : Number(r.cpuPercent)} label="CPU" /></td>
                  <td>
                    <Meter value={ratio(r.memUsedBytes, r.memTotalBytes)} label="Memory" />
                    <div className="muted">{bytes(r.memUsedBytes)} / {bytes(r.memTotalBytes)}</div>
                  </td>
                  <td>
                    <Meter value={ratio(r.diskUsedBytes, r.diskTotalBytes)} label="Disk" />
                    <div className="muted">{bytes(r.diskUsedBytes)} / {bytes(r.diskTotalBytes)}</div>
                  </td>
                  {/* Each row shows what that server's invoice says, in its own
                      currency — never converted. Conversion happens once, in the
                      footer, and is labelled as such. A server with no price
                      shows as unknown, never as zero. */}
                  <td className="num">
                    {r.monthlyCost === null
                      ? <span className="muted small">not set</span>
                      : money(r.monthlyCost, r.costCurrency)}
                  </td>
                  <td title={r.sampledAt ?? ''}>{ago(r.sampledAt)}</td>
                </tr>
              ))}
            </tbody>
            {/* A real <tfoot>, so the total belongs to the column it totals and
                a screen reader reads it as part of the table rather than as a
                stray paragraph underneath. */}
            <tfoot>
              <tr className="total-row">
                <td colSpan={5}>
                  Total monthly <span className="muted small">in CAD</span>
                  {data.cost.unpriced > 0 && (
                    <span className="muted small">
                      {' '}· excludes {data.cost.unpriced} server
                      {data.cost.unpriced === 1 ? '' : 's'} with no price
                    </span>
                  )}
                  {/* Named, never silently dropped: a total missing a currency
                      is too small, and saying so is the difference between a
                      caveat and a wrong number. */}
                  {data.cost.missingRates.length > 0 && (
                    <span className="form-error small">
                      {' '}· no rate for {data.cost.missingRates.join(', ')} — excluded
                    </span>
                  )}
                </td>
                <td className="num">
                  {data.cost.totalCad === null ? (
                    <span className="muted small">no rates set</span>
                  ) : (
                    <strong>{money(data.cost.totalCad, 'CAD')}</strong>
                  )}
                  {/* The per-currency figures are what the invoices say; the CAD
                      total above is a conversion. Both are shown so the fact and
                      the derivation are never confused. */}
                  {data.cost.byCurrency.length > 1 &&
                    data.cost.byCurrency.map((t) => (
                      <div key={t.currency} className="muted small">
                        {money(t.total, t.currency)}
                      </div>
                    ))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        {/* The rate basis, stated. A converted total without one is a number
            nobody can check or reproduce. */}
        <p className="muted small">
          {data.cost.totalCad === null ? (
            <>
              Set <code>FX_RATES_TO_CAD</code> (e.g. <code>USD:1.39,EUR:1.50,AUD:0.92</code>) to
              show a combined total. Until then each currency is listed on its own.
            </>
          ) : (
            <>
              Converted at{' '}
              {Object.entries(data.cost.rates)
                .filter(([c]) => c !== 'CAD')
                .map(([c, r]) => `${c} ${r}`)
                .join(' · ')}
              {data.cost.ratesAsAt ? ` · as at ${data.cost.ratesAsAt}` : ' · rate date not recorded'}.
              Per-currency figures are what the invoices say; the CAD total is a conversion.
            </>
          )}
        </p>
      </Card>

      {/* Shown only when there is something to say. An always-present panel
          reading "no failures" trains people to stop looking at it. */}
      {data.recentFailures.length > 0 && (
        <Card title="Recent collection failures">
          <ul className="failures">
            {data.recentFailures.map((f, i) => (
              <li key={`${f.hostname}-${i}`}>
                <StatusPill status="UNREACHABLE" label={f.server} />
                <span className="mono muted">{f.hostname}</span>
                <span>{f.error ?? 'unknown error'}</span>
                <span className="muted">{ago(f.at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.resources.length === 0 && <Empty>No servers are being collected yet.</Empty>}
    </>
  );
}
